import { useState, useEffect } from 'react';
import { getAuth, setCurrentAccount } from '../lib/auth';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Building2, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import api from '../lib/api';

export const AccountSwitcher = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data } = await api.get('/auth/accounts');
        setAccounts(data);
        localStorage.setItem('accounts', JSON.stringify(data));
        if (currentAccount) {
          const updated = data.find(a => a.id === currentAccount.id);
          if (updated) localStorage.setItem('current_account', JSON.stringify(updated));
        } else if (data.length > 0) {
          localStorage.setItem('current_account', JSON.stringify(data[0]));
        }
      } catch {
        const { accounts: cached } = getAuth();
        setAccounts(cached);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchAccount = (account) => {
    setCurrentAccount(account);
    setOpen(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <Button variant="outline" className="rounded-none" disabled data-testid="account-switcher-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  if (!accounts || accounts.length === 0) return null;

  const displayAccount = accounts.find(a => a.id === currentAccount?.id) || currentAccount || accounts[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="rounded-none flex items-center gap-2 w-48 max-w-[12rem] justify-between"
          data-testid="account-switcher"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium text-xs truncate" title={displayAccount?.organisation_name || ''}>
              {displayAccount?.organisation_name || 'Select Account'}
            </span>
          </div>
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-none" align="end">
        <Command
          filter={(value, search) => {
            // value is the item's `value` prop (we put the org name + account_no there)
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search organisation..." data-testid="account-switcher-search" />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>No organisation found.</CommandEmpty>
            <CommandGroup heading="Switch Account">
              {accounts.map((account) => (
                <CommandItem
                  key={account.id}
                  value={`${account.organisation_name} ${account.account_no || ''}`}
                  onSelect={() => switchAccount(account)}
                  data-testid={`account-${account.id}`}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{account.organisation_name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate">{account.account_no}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Role: <span className="capitalize">{account.role}</span>
                      </div>
                    </div>
                    {displayAccount?.id === account.id && (
                      <Check className="h-4 w-4 flex-shrink-0" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
